import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Input,
  Select,
  Option,
  CircularProgress,
  Alert,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  Textarea,
} from '@mui/joy';
import {
  Plus as Add,
  MagnifyingGlass as Search,
  PencilSimple as Edit,
  Trash as Delete,
  ArrowsClockwise as Refresh,
  Bank as AccountBalance,
  CaretLineLeft as FirstPage,
  CaretLineRight as LastPage,
  CaretLeft as NavigateBefore,
  CaretRight as NavigateNext,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { ChartOfAccount, ChartOfAccountFormData, AccountType, AccountSubtype } from '../../types/accounting';
import { useNotification } from '../../contexts/NotificationContext';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const getAccountTypeColor = (type: AccountType) => {
  switch (type) {
    case 'ASSET': return 'primary';
    case 'LIABILITY': return 'warning';
    case 'EQUITY': return 'neutral';
    case 'REVENUE': return 'success';
    case 'EXPENSE': return 'danger';
    default: return 'neutral';
  }
};

export const ChartOfAccounts: React.FC = () => {
  const { showNotification } = useNotification();
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ChartOfAccount | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const [formData, setFormData] = useState<ChartOfAccountFormData>({
    account_number: '',
    name: '',
    account_type: 'ASSET' as AccountType,
    account_subtype: 'BANK' as AccountSubtype,
    description: '',
  });

  const fetchAccounts = async (page?: number, size?: number) => {
    const actualPage = page ?? currentPage;
    const actualSize = size ?? pageSize;
    console.log('fetchAccounts called with:', { actualPage, actualSize, currentPage, pageSize });

    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: actualPage,
        page_size: actualSize === -1 ? 1000 : actualSize, // -1 means "All"
      };
      if (searchTerm) params.search = searchTerm;
      if (filterType) params.account_type = filterType;

      const response = await apiAccountingClient.getChartOfAccounts(params);
      console.log('API response:', {
        resultsLength: response.results.length,
        count: response.count,
        requestedPageSize: actualSize,
        actualPage: actualPage
      });
      setAccounts(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError('Failed to load chart of accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [currentPage, pageSize, searchTerm, filterType]);

  // Reset to first page when filters change (but not pageSize, as that's handled in onChange)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const handleCreateAccount = async () => {
    try {
      await apiAccountingClient.createAccount(formData);
      showNotification('success', 'Account created successfully');
      setShowModal(false);
      fetchAccounts();
      resetForm();
    } catch (err: any) {
      console.error('Create account error:', err);
      const message = err.response?.data?.detail || err.message || 'Failed to create account';
      showNotification('error', message);
    }
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;

    try {
      await apiAccountingClient.updateAccount(editingAccount.id, formData);
      showNotification('success', 'Account updated successfully');
      setShowModal(false);
      fetchAccounts();
      resetForm();
    } catch (err: any) {
      console.error('Update account error:', err);
      const message = err.response?.data?.detail || err.message || 'Failed to update account';
      showNotification('error', message);
    }
  };

  const handleDeleteAccount = async (id: number) => {
    console.log('Delete button clicked for account:', id);
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await apiAccountingClient.deleteAccount(id);
        showNotification('success', 'Account deleted successfully');
        fetchAccounts();
      } catch (err: any) {
        console.error('Delete account error:', err);
        const message = err.response?.data?.detail || err.message || 'Failed to delete account';
        showNotification('error', message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      account_number: '',
      name: '',
      account_type: 'ASSET' as AccountType,
      account_subtype: 'BANK' as AccountSubtype,
      description: '',
    });
    setEditingAccount(null);
  };

  const openEditModal = (account: ChartOfAccount) => {
    console.log('Edit button clicked for account:', account);
    setEditingAccount(account);
    setFormData({
      account_number: account.account_number,
      name: account.name,
      account_type: account.account_type,
      account_subtype: account.account_subtype,
      description: account.description || '',
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Box><Typography level="h1">Chart of accounts</Typography><Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>Organize the accounts used to record assets, liabilities, income, and expenses.</Typography></Box>
        <Box sx={{ display: 'flex', gap: 1, '& > button': { flex: { xs: 1, sm: '0 0 auto' } } }}>
          <Button
            startDecorator={<Add />}
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            New <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>Account</Box>
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Refresh />}
            onClick={() => fetchAccounts()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, p: 2 }}>
          <Input
            placeholder="Search accounts..." aria-label="Search accounts"
            startDecorator={<Search />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by type"
            value={filterType}
            onChange={(_, value) => setFilterType(value || '')}
            sx={{ minWidth: { xs: 0, sm: 200 } }}
          >
            <Option value="">All Types</Option>
            <Option value="ASSET">Assets</Option>
            <Option value="LIABILITY">Liabilities</Option>
            <Option value="EQUITY">Equity</Option>
            <Option value="REVENUE">Revenue</Option>
            <Option value="EXPENSE">Expenses</Option>
          </Select>
        </Box>
      </Card></CollapsibleFilters>

      {/* Accounts Table */}
      <Card>
        <ResponsiveTable<ChartOfAccount>
          rows={accounts}
          getRowKey={(a) => a.id}
          emptyState={<Typography level="body-sm" sx={{ textAlign: 'center', p: 4 }}>No accounts found</Typography>}
          rowActions={(account) => (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="sm" variant="plain" onClick={() => openEditModal(account)} disabled={account.is_system_account}>
                <Edit size={18} />
              </IconButton>
              <IconButton size="sm" variant="plain" color="danger" onClick={() => handleDeleteAccount(account.id)} disabled={account.is_system_account}>
                <Delete size={18} />
              </IconButton>
            </Box>
          )}
          columns={[
            { key: 'name', sortValue: (row) => row.name, header: 'Account Name', mobileVariant: 'header', render: (a) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalance size={16} color="var(--joy-palette-text-secondary)" />
                <Typography level="title-sm">{a.name}</Typography>
              </Box>
            )},
            { key: 'account_number', sortValue: (row) => row.account_number, header: 'Account #', width: '120px', mobileLabel: 'Account #', render: (a) => a.account_number },
            { key: 'type', sortValue: (row) => row.account_type, header: 'Type', render: (a) => <Chip size="sm" variant="soft" color={getAccountTypeColor(a.account_type)}>{a.account_type}</Chip> },
            { key: 'subtype', sortValue: (row) => row.account_subtype, header: 'Subtype', render: (a) => a.account_subtype.replace(/_/g, ' ') },
            { key: 'balance', sortValue: (row) => Number(row.current_balance), header: 'Balance', render: (a) => <Typography level="body-sm" sx={{ fontWeight: 'md' }}>{formatCurrency(a.current_balance)}</Typography> },
            { key: 'status', sortValue: (row) => row.is_active, header: 'Status', render: (a) => <Chip size="sm" variant="soft" color={a.is_active ? 'success' : 'neutral'}>{a.is_active ? 'Active' : 'Inactive'}</Chip> },
          ]}
        />

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Page Size Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm">Show:</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, value) => {
                  if (value) {
                    console.log('onChange: changing pageSize from', pageSize, 'to', value);
                    setPageSize(value);
                    setCurrentPage(1);
                    // Force immediate fetch with new values
                    fetchAccounts(1, value);
                  }
                }}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
                <Option value={-1}>All</Option>
              </Select>
              <Typography level="body-sm" sx={{ ml: 2 }}>
                {pageSize === -1
                  ? `Showing all ${totalCount} accounts`
                  : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalCount)}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} accounts`
                }
              </Typography>
            </Box>

            {/* Navigation Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <FirstPage />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <NavigateBefore />
              </IconButton>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                if (page < 1 || page > totalPages) return null;

                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outlined'}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              })}

              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <NavigateNext />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(totalPages)}
              >
                <LastPage />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <ModalDialog size="md" sx={{ minWidth: { xs: 'auto', sm: 500 }, maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">
            {editingAccount ? 'Edit Account' : 'Create New Account'}
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl required>
              <FormLabel>Account Number</FormLabel>
              <Input
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="e.g., 1000"
              />
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                Must be at least 4 digits and unique within your organization
              </Typography>
            </FormControl>

            <FormControl required>
              <FormLabel>Account Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Cash - Operating Account"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Account Type</FormLabel>
              <Select
                value={formData.account_type}
                onChange={(_, value) => {
                  if (value) {
                    // Set default subtype when account type changes
                    let defaultSubtype: AccountSubtype = 'BANK' as AccountSubtype;
                    switch(value) {
                      case 'ASSET': defaultSubtype = 'BANK' as AccountSubtype; break;
                      case 'LIABILITY': defaultSubtype = 'ACCOUNTS_PAYABLE' as AccountSubtype; break;
                      case 'EQUITY': defaultSubtype = 'EQUITY_GENERAL' as AccountSubtype; break;
                      case 'REVENUE': defaultSubtype = 'INCOME' as AccountSubtype; break;
                      case 'EXPENSE': defaultSubtype = 'EXPENSE_GENERAL' as AccountSubtype; break;
                    }
                    setFormData({ ...formData, account_type: value as AccountType, account_subtype: defaultSubtype });
                  }
                }}
              >
                <Option value="ASSET">Asset</Option>
                <Option value="LIABILITY">Liability</Option>
                <Option value="EQUITY">Equity</Option>
                <Option value="REVENUE">Revenue</Option>
                <Option value="EXPENSE">Expense</Option>
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Account Subtype</FormLabel>
              <Select
                value={formData.account_subtype}
                onChange={(_, value) => value && setFormData({ ...formData, account_subtype: value as AccountSubtype })}
              >
                {formData.account_type === 'ASSET' && (
                  <>
                    <Option value="BANK">Bank</Option>
                    <Option value="ACCOUNTS_RECEIVABLE">Accounts Receivable</Option>
                    <Option value="OTHER_CURRENT_ASSET">Other Current Asset</Option>
                    <Option value="FIXED_ASSET">Fixed Asset</Option>
                    <Option value="OTHER_ASSET">Other Asset</Option>
                  </>
                )}
                {formData.account_type === 'LIABILITY' && (
                  <>
                    <Option value="ACCOUNTS_PAYABLE">Accounts Payable</Option>
                    <Option value="CREDIT_CARD">Credit Card</Option>
                    <Option value="OTHER_CURRENT_LIABILITY">Other Current Liability</Option>
                    <Option value="LONG_TERM_LIABILITY">Long Term Liability</Option>
                  </>
                )}
                {formData.account_type === 'EQUITY' && (
                  <>
                    <Option value="EQUITY_GENERAL">Equity</Option>
                    <Option value="RETAINED_EARNINGS">Retained Earnings</Option>
                  </>
                )}
                {formData.account_type === 'REVENUE' && (
                  <>
                    <Option value="INCOME">Income</Option>
                    <Option value="OTHER_INCOME">Other Income</Option>
                  </>
                )}
                {formData.account_type === 'EXPENSE' && (
                  <>
                    <Option value="COST_OF_GOODS_SOLD">Cost of Goods Sold</Option>
                    <Option value="EXPENSE_GENERAL">Expense</Option>
                    <Option value="OTHER_EXPENSE">Other Expense</Option>
                  </>
                )}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                minRows={2}
                placeholder="Optional description"
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={editingAccount ? handleUpdateAccount : handleCreateAccount}
              >
                {editingAccount ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
